/* eslint-disable no-unsafe-optional-chaining */
import PropTypes from 'prop-types';
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  MdArrowDownward,
  MdArrowUpward,
  MdOutlineFilterList,
  MdSearch,
  MdSource,
} from 'react-icons/md';

import AppliedFilters from '../../filters/AppliedFilters/AppliedFilters';
import { CompositeFilter } from '../../filters/CompositeFilter';

import api, { authHeader } from '../../../../../services/API/apiConfig';
import {
  AttachmentStyled,
  ButtonInputStyled,
  DivNoDataStyled,
  FilterButtonStyled,
  IconContentStyled,
  LoaderContainerStyled,
  MiddleOfThePageStyled,
  SearchAndFilterStyled,
  SearchStyled,
  SwitchStyled,
  TableCellBodyStyled,
  TableCellHeaderStyled,
  TableRowStyled,
  TableStyled,
  TextFieldStyled,
  TotalAmountStyled,
} from './index.styles';

import { TourContext } from '../../../../../context/tour';
import { PAYABLES_MOCK } from '../../../../../services/driverJS/mocks';

export function TablePayables(props) {
  const {
    filter,
    setFilter,
    constructionId,
    headings,
    callbackClickRow = () => {},
    filterData,
    setFilterData,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { isTourOn } = useContext(TourContext);

  const user = JSON.parse(localStorage.getItem('user'));

  const [dados, setDados] = useState([]);
  const [totalItems, setTotalItems] = useState(dados.length);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [open, setOpen] = useState(false);

  const [activeFilter, setActiveFilter] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [order, setOrder] = useState(1);
  const [columnOrder, setColumnOrder] = useState(
    params.id ? 'Vencimento' : 'Lançamento'
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  function changeTotalAmount(arr) {
    const values = arr.map((item) =>
      Number(
        item.total
          .replace('R$', '')
          .replace(' ', '')
          .replace('.', '')
          .replace(',', '.')
      )
    );

    const total = values.reduce((prev, curr) => prev + curr, 0);
    setTotalAmount(total);
  }

  const getPayables = () => {
    if (constructionId) {
      api
        .get(`/constructions/${constructionId}/payables`, {
          headers: authHeader(),
        })
        .then((response) => {
          const { data } = response?.data;

          setDados(data);
          setFilter(data);
          changeTotalAmount(data);
          setIsLoading(false);
        });
    } else {
      api
        .get('/payables', {
          headers: authHeader(),
        })
        .then((response) => {
          const { data, sum } = response?.data;

          setTotalAmount(parseFloat(sum));
          setDados(data);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    getPayables();
  }, []);

  function handleStatusChange(event, id) {
    let d = {};

    const list = dados.map((payable) => {
      if (payable.id === id) {
        const newStatus = payable.status_id === 1 ? 0 : 1;
        d = { status: newStatus };
        return { ...payable, status_id: newStatus };
      }

      return payable;
    });

    setDados(list);
    api.patch(`/payables/${id}`, d, {
      headers: authHeader(),
    });
  }

  function handleDuplicatePayable(id) {
    navigate(`/new-release/duplicate/${id}`, {
      state: { from: location.pathname },
    });
  }

  useEffect(() => {
    if (constructionId) {
      api
        .get(`/constructions/${constructionId}/filters`, {
          headers: authHeader(),
        })
        .then((res) => {
          const { data } = res?.data;
          setFilter(data);
          const validFilters = Object.entries(data).filter(
            (item) =>
              item[1] !== null &&
              item[0] !== 'id' &&
              item[0] !== 'user_id' &&
              item[0] !== 'construction_id'
          );
          if (validFilters.length) {
            setActiveFilter(true);
          } else {
            setActiveFilter(false);
          }
        });
    } else {
      api
        .get('/filters', {
          headers: authHeader(),
        })
        .then((res) => {
          const { data } = res?.data;
          setFilter(data);
          const validFilters = Object.entries(data).filter(
            (item) =>
              item[1] !== null &&
              item[0] !== 'id' &&
              item[0] !== 'user_id' &&
              item[0] !== 'construction_id'
          );
          if (validFilters.length) {
            setActiveFilter(true);
          } else {
            setActiveFilter(false);
          }
        });
    }
  }, [dados]);

  const handleOrder = (fieldName) => {
    setOrder(-order);

    setColumnOrder(fieldName);

    if (fieldName === 'Vencimento') {
      setFilterData(
        dados?.sort((a, b) => {
          const dataA = new Date(a.due.split('/').reverse().join('-'));
          const dataB = new Date(b.due.split('/').reverse().join('-'));
          return dataA < dataB ? -order : order;
        })
      );
    }

    if (fieldName === 'Fornecedor') {
      setFilterData(
        dados?.sort(
          (a, b) => a.supplier.name.localeCompare(b.supplier.name) * order
        )
      );
    }
    if (fieldName === 'Descrição') {
      setFilterData(
        dados?.sort((a, b) => {
          const descA = (a.description || '').toLowerCase();
          const descB = (b.description || '').toLowerCase();
          return descA.localeCompare(descB) * order;
        })
      );
    }
    if (fieldName === 'Obra') {
      setFilterData(
        dados?.sort(
          (a, b) =>
            a.construction.name.localeCompare(b.construction.name) * order
        )
      );
    }
    if (fieldName === 'Status') {
      setFilterData(
        dados?.sort((a, b) => (a.status < b.status ? -order : order))
      );
    }
    if (fieldName === 'Item') {
      setFilterData(
        dados?.sort((a, b) =>
          a.payable_items.length < b.payable_items.length ? -order : order
        )
      );
    }
    if (fieldName === 'Item de Orçamento') {
      setFilterData(
        dados?.sort((a, b) =>
          a.payable_items.length < b.payable_items.length ? -order : order
        )
      );
    }
    if (fieldName === 'Lançamento') {
      setFilterData(
        dados?.sort((a, b) => {
          const dataA = new Date(a.created_at.split('/').reverse().join('-'));
          const dataB = new Date(b.created_at.split('/').reverse().join('-'));
          return dataA < dataB ? -order : order;
        })
      );
    }
    if (fieldName === 'Valor') {
      setFilterData(
        dados?.sort((a, b) => {
          const dataA = parseFloat(
            a.total.replace('R$ ', '').replace('.', '').replace(',', '.')
          );
          const dataB = parseFloat(
            b.total.replace('R$ ', '').replace('.', '').replace(',', '.')
          );
          return dataA < dataB ? -order : order;
        })
      );
    }
    if (fieldName === 'Valor') {
      setFilterData(
        dados?.sort((a, b) => {
          const dataA = parseFloat(
            a.total.replace('R$ ', '').replace('.', '').replace(',', '.')
          );
          const dataB = parseFloat(
            b.total.replace('R$ ', '').replace('.', '').replace(',', '.')
          );
          return dataA < dataB ? -order : order;
        })
      );
    }
    if (fieldName === 'Anexos') {
      setFilterData(
        dados?.sort((a, b) =>
          a.total_attachments < b.total_attachments ? -order : order
        )
      );
    }
  };

  useEffect(() => {
    setTotalItems(dados?.length);
  }, [dados]);

  const handleSearch = () => {
    const search = searchTerm.toLowerCase();
    const filtered = dados.filter((data) => {
      if (
        searchTerm === '' ||
        data.supplier?.name.toLowerCase().includes(search) ||
        data.description?.toLowerCase().includes(search)
      ) {
        return true;
      }

      if (constructionId === null) {
        if (
          (data.construction !== null &&
            data.construction.name !== null &&
            data.construction.name.toLowerCase().includes(search)) ||
          (data.due !== null && data.due.toLowerCase().includes(search))
        ) {
          return true;
        }
      }

      return false;
    });

    setFilterData(filtered);
    setTotalItems(filtered.length);

    if (searchTerm === '') {
      changeTotalAmount(dados);
    } else {
      changeTotalAmount(filtered);
    }
  };

  useEffect(() => {
    handleSearch();
  }, [searchTerm, dados]);

  return (
    <>
      <div>
        {activeFilter && (
          <AppliedFilters
            setFilter={setFilter}
            filter={filter}
            getPayables={getPayables}
          />
        )}
        <MiddleOfThePageStyled>
          <CompositeFilter
            getPayables={getPayables}
            open={open}
            onClose={handleClose}
            setActiveFilter={setActiveFilter}
            payables={dados}
          />

          <Typography
            style={{
              fontSize: activeFilter ? '16px' : '18px',
              lineHeight: '19px',
              fontWeight: 500,
            }}
            variant="h2"
            id="payables-total"
          >
            {`Lançamentos: ${totalItems}`}
          </Typography>

          <SearchAndFilterStyled>
            <SearchStyled id="search">
              <TextFieldStyled
                type="search"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <ButtonInputStyled variant="contained">
                <MdSearch size={17} />
              </ButtonInputStyled>
            </SearchStyled>
            <FilterButtonStyled
              id="filter-icon"
              onClick={handleOpen}
              size="large"
            >
              <MdOutlineFilterList
                size={33}
                className={!filter ? 'active' : ''}
              />
            </FilterButtonStyled>
          </SearchAndFilterStyled>
        </MiddleOfThePageStyled>
      </div>
      {isLoading ? (
        <LoaderContainerStyled>
          <Typography variant="h2" style={{ fontSize: 25 }}>
            Carregando...
          </Typography>
          <LinearProgress style={{ width: '80%' }} />
        </LoaderContainerStyled>
      ) : (
        <Paper>
          <TableStyled id="home-view">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {headings?.map((label) => {
                    const headersWidth = {
                      Vencimento: '10%',
                      Fornecedor: '20%',
                      Descrição: '25%',
                      Obra: '15%',
                      Item: '6%',
                      Valor: '8%',
                      Status: '6%',
                      Lançamento: '6%',
                      Anexos: '6%',
                      Duplicar: '3%',
                    };

                    const columnsIds = {
                      Vencimento: 'due-column',
                      Fornecedor: 'supplier-column',
                      Descrição: 'description-column',
                      Obra: 'construction-column',
                      Item: 'item-column',
                      Valor: 'value-column',
                      Status: 'status-column',
                      Lançamento: 'date-column',
                      Anexos: 'attachments-column',
                      Duplicar: 'duplicate-column',
                    };

                    return (
                      <TableCellHeaderStyled
                        key={label}
                        style={{ width: headersWidth[label] }}
                        onClick={() =>
                          label !== 'Duplicar' && handleOrder(label)
                        }
                        id={columnsIds[label]}
                      >
                        <div
                          style={{
                            cursor:
                              label === 'Duplicar' ? 'default' : 'pointer',
                          }}
                        >
                          {label}{' '}
                          {columnOrder === label &&
                            (order === -1 ? (
                              <MdArrowDownward size={18} />
                            ) : (
                              <MdArrowUpward size={18} />
                            ))}
                        </div>
                      </TableCellHeaderStyled>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {isTourOn
                  ? PAYABLES_MOCK.map((data) => (
                      <TableRowStyled key={data.id}>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                          style={{ textAlign: 'center' }}
                        >
                          {data.due}
                        </TableCellBodyStyled>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                        >
                          {data.supplier.name}
                        </TableCellBodyStyled>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                        >
                          {data.description}
                        </TableCellBodyStyled>
                        {constructionId === null && (
                          <TableCellBodyStyled
                            onClick={() => callbackClickRow(data.id)}
                          >
                            {data.construction && data.construction.name}
                          </TableCellBodyStyled>
                        )}
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                          style={{ textAlign: 'center' }}
                        >
                          {data.payable_items.length}
                        </TableCellBodyStyled>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                        >
                          {data.total}
                        </TableCellBodyStyled>
                        <TableCell>
                          <FormGroup
                            aria-owns={open ? 'mouse-over-popover' : undefined}
                          >
                            <Tooltip
                              title={data.status_id === 0 ? 'Pendente' : 'Pago'}
                            >
                              <SwitchStyled
                                checked={data.status_id !== 0}
                                onChange={(e) => handleStatusChange(e, data.id)}
                                inputProps={{ 'aria-label': 'Switch A' }}
                              />
                            </Tooltip>
                          </FormGroup>
                        </TableCell>
                        <TableCellBodyStyled style={{ textAlign: 'center' }}>
                          {data.created_at}
                        </TableCellBodyStyled>
                        <TableCell onClick={() => callbackClickRow(data.id)}>
                          <AttachmentStyled>
                            {data.total_attachments}
                          </AttachmentStyled>
                        </TableCell>
                        {user.user_type !== 'client' && (
                          <TableCellBodyStyled>
                            <IconButton
                              onClick={() => handleDuplicatePayable(data.id)}
                              size="large"
                            >
                              <IconContentStyled size={24} />
                            </IconButton>
                          </TableCellBodyStyled>
                        )}
                      </TableRowStyled>
                    ))
                  : dados?.length > 0 &&
                    filterData?.map((data) => (
                      <TableRowStyled key={data.id}>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                          style={{ textAlign: 'center' }}
                        >
                          {data.due}
                        </TableCellBodyStyled>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                        >
                          {data.supplier.name}
                        </TableCellBodyStyled>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                        >
                          {data.description}
                        </TableCellBodyStyled>
                        {constructionId === null && (
                          <TableCellBodyStyled
                            onClick={() => callbackClickRow(data.id)}
                          >
                            {data.construction && data.construction.name}
                          </TableCellBodyStyled>
                        )}
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                          style={{ textAlign: 'center' }}
                        >
                          {data.payable_items.length}
                        </TableCellBodyStyled>
                        <TableCellBodyStyled
                          onClick={() => callbackClickRow(data.id)}
                        >
                          {data.total}
                        </TableCellBodyStyled>
                        <TableCell>
                          <FormGroup
                            aria-owns={open ? 'mouse-over-popover' : undefined}
                          >
                            <Tooltip
                              title={data.status_id === 0 ? 'Pendente' : 'Pago'}
                            >
                              <SwitchStyled
                                checked={data.status_id !== 0}
                                onChange={(e) => handleStatusChange(e, data.id)}
                                inputProps={{ 'aria-label': 'Switch A' }}
                              />
                            </Tooltip>
                          </FormGroup>
                        </TableCell>
                        <TableCellBodyStyled style={{ textAlign: 'center' }}>
                          {data.created_at}
                        </TableCellBodyStyled>
                        <TableCell onClick={() => callbackClickRow(data.id)}>
                          <AttachmentStyled>
                            {data.total_attachments}
                          </AttachmentStyled>
                        </TableCell>
                        {user.user_type !== 'client' && (
                          <TableCellBodyStyled>
                            <IconButton
                              onClick={() => handleDuplicatePayable(data.id)}
                              size="large"
                            >
                              <IconContentStyled size={24} />
                            </IconButton>
                          </TableCellBodyStyled>
                        )}
                      </TableRowStyled>
                    ))}
              </TableBody>
            </Table>
            {!filterData.length && !isTourOn && (
              <DivNoDataStyled>
                <MdSource size={200} color="#B9B9B9" />
                <Typography
                  variant="h3"
                  style={{
                    color: '#1c1c1c',
                    fontSize: '16px',
                    marginBottom: '32px',
                  }}
                >
                  Nenhum lançamento encontrado.
                </Typography>
              </DivNoDataStyled>
            )}
          </TableStyled>
          <TotalAmountStyled>
            <Typography variant="h3">VALOR TOTAL</Typography>
            <Typography variant="h3">
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(filterData.length ? totalAmount : 0)}
            </Typography>
          </TotalAmountStyled>
        </Paper>
      )}
    </>
  );
}

TablePayables.propTypes = {
  filter: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      construction_id: PropTypes.number,
      creation_date: PropTypes.string,
      date_range: PropTypes.string,
      due_date: PropTypes.string,
      id: PropTypes.number,
      last_payables: PropTypes.string,
      status: PropTypes.string,
      supplier: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      total: PropTypes.string,
      user_id: PropTypes.number,
    })
  ),
  setFilter: PropTypes.func.isRequired,
  constructionId: PropTypes.string,
  headings: PropTypes.arrayOf(PropTypes.string).isRequired,
  callbackClickRow: PropTypes.func.isRequired,
};

TablePayables.defaultProps = {
  filter: null,
  constructionId: null,
};
